import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { ComponentParamaterType, ComponentType, MessageType } from '@adornis/whatsapp/server/types.js';
import { DateTime } from 'luxon';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import {
  getWhatsappTemplate,
  InstitutionMailTemplate,
  MailTemplateType,
  MailTemplateVars,
} from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { Product } from '../entities/products.js';
import { Setting, SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { isDefined } from '../util.js';
import { getNotificationType, sendWhatsapp } from './_helper.js';

defineMailTemplate(MailTemplateType.NACHRUECKPLATZ_VERFUEGBAR, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Nachrückplatz verfügbar',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>gute Neuigkeiten, soeben ist ein Platz in unserer Veranstaltung frei geworden. Sie können sich jetzt in Ihrem Profil anmelden, solange noch Plätze verfügbar sind.</p>
<p>Nachstehend f&uuml;hren wir nochmals die wesentlichen Informationen der Veranstaltung auf:</p>
<p>{{changed-registrations}}</p>
<p>Eine Auflistung all Ihrer Workshops und die M&ouml;glichkeit, sich wieder abmelden zu k&ouml;nnen, finden Sie in Ihrer Anmeldeverwaltung: {{login-link}}</p>
<p><strong>Sie k&ouml;nnen nicht teilnehmen? Bitte abmelden und dadurch anderen Studierenden die Teilnahme erm&ouml;glichen</strong></p>
<p>Falls Sie den Termin nicht wahrnehmen k&ouml;nnen, bitten wir Sie, sich zeitnah in ihrer Anmeldeverwaltung von der Veranstaltung abzumelden. Studierende auf der Warteliste profitieren dann von Ihrer Abmeldung.</p>
<p>Eine Abmeldung ist nur bis sp&auml;testens 6 Tage vor Workshop-Beginn m&ouml;glich. Nach Ablauf dieser Frist ist eine Abmeldung nur <a href=\"mailto:contact@anmeldung.uni-frankfurt.de\">per Mail</a> mit Angabe eines triftigen Grundes m&ouml;glich. Bei Nichterscheinen ohne vorherige Absage behalten wir uns vor, Sie von weiteren Workshops auszuschlie&szlig;en bzw. Ihnen entstehende Kosten in Rechnung zu stellen.</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>Good news, a spot has just become available in our event. You are now able to enter the register for the event on your profile page as long as there are still spots available</p>
<p>Below we list again the essential information of the event:</p>
<p>{{changed-registrations}}</p>
<p>You will find a list of all your workshops and the possibility to cancel your registration in your registration administration: {{login-link}}</p>
<p><strong>You are unable to attend? Please deregister and thereby enable other students to participate.</strong> If you are unable to attend the event, please deregister promptly in your registration administration. Students on the waiting list will then benefit from your deregistration.</p>
<p>Deregistration is only possible up to 6 days before the start of the workshop. After this deadline, cancellation is only possible by e-mail with a valid reason. In the case of non-attendance without prior cancellation, we reserve the right to exclude you from further workshops or to charge you for any costs incurred.</p>
<p>Best regards</p>`,
    footer: '[footer]',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.NACHRUECKPLATZ_VERFUEGBAR),
  attachments: [],
  displayName: 'Nachrückplatz verfügbar',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.NACHRUECKPLATZ_VERFUEGBAR),
});

export async function notifyNachrueckplatzVerfuegbar(participationIDs: string[]) {
  const participations = await getByID(
    Participation,
    participationIDs,
  )({
    state: 1,
    productID: 1,
    waitingListPlace: 1,
    user: User.allFields,
    product: { institutionID: 1, leftCapacity: 1 },
  });

  if (!participations || participations.length === 0) throw new Error('Participation not found');
  const participation = participations[0];
  if (!participation) throw new Error('Participation not found');
  if (!participation.product) throw new Error('Participation has no product');
  const institutionID = participation.product.institutionID;
  const institution = await getByID(
    Institution,
    institutionID,
  )({
    ...Institution.allFields,
    colorDefintions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('could not find institution');
  const user = participation.user;
  if (!user) throw new Error('could not find user');

  // set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = (await InstitutionMailTemplate.getMailTemplateForInstitution(
    institutionID,
    MailTemplateType.NACHRUECKPLATZ_VERFUEGBAR,
  )(InstitutionMailTemplate.allFields)) as InstitutionMailTemplate;

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const otherParticipations = (await loadMongoData(Participation, { userID: user._id })).filter(
    p => !participations.map(_p => p._id).includes(p._id),
  );
  const otherProducts = await getByID(
    Product,
    otherParticipations.map(p => p.productID as string),
  )({
    _class: 1,
    _id: 1,
    name: 1,
    description: 1,
    translatedDescriptionHTML: { _class: 1, langKey: 1, text: 1 },
    sortingWeight: 1,
    hasPrice: 1,
    categoryID: 1,
    institutionID: 1,
    timeframeID: 1,
    attachedCertificate: { _class: 1, certificateID: 1, exportDate: 1 },
    lastReminderMailSent: 1,
    publishDate: 1,
    topic: 1,
    language: 1,
    presenceType: 1,
    isCanceled: 1,
    appointments: {
      _class: 1,
      _id: 1,
      start: 1,
      end: 1,
      isNonBlocking: 1,
      location: 1,
      name: 1,
    },
    capacity: 1,

    hideCapacity: 1,
    settings: Setting.allFields,
  });

  for (const p of participations)
    p.product = await getByID(
      Product,
      p.productID,
    )({
      _class: 1,
      _id: 1,
      name: 1,
      description: 1,
      translatedDescriptionHTML: { _class: 1, langKey: 1, text: 1 },
      sortingWeight: 1,
      hasPrice: 1,
      categoryID: 1,
      institutionID: 1,
      timeframeID: 1,
      attachedCertificate: { _class: 1, certificateID: 1, exportDate: 1 },
      lastReminderMailSent: 1,
      publishDate: 1,
      topic: 1,
      language: 1,
      presenceType: 1,
      isCanceled: 1,
      appointments: {
        _class: 1,
        _id: 1,
        start: 1,
        end: 1,
        isNonBlocking: 1,
        location: 1,
        name: 1,
      },
      capacity: 1,

      hideCapacity: 1,
      settings: Setting.allFields,
    });
  for (const p of otherParticipations) p.product = otherProducts.find(p2 => p._id === p2._id);

  const start = participations[0]?.product.appointments?.[0]?.start;
  const daysTillEvent = start?.diff(DateTime.now()).as('days').toFixed(0);
  let dates = participations[0]?.product.appointments?.map(a => a.start.toFormat('dd.LL.yyyy')).join(', ');
  if (!dates) dates = start.toFormat('dd.LL.yyyy');

  const mailVars = {
    [MailTemplateVars.EVENT_TITLE]:
      participations
        .map(p => p?.product?.name)
        ?.filter(isDefined)
        ?.join(', ') ?? '',
    [MailTemplateVars.EVENT_DATES]: dates,
    [MailTemplateVars.EVENT_LEAD_DAYS]: daysTillEvent,
    [MailTemplateVars.TITLE]: user.title ?? '',
    [MailTemplateVars.FIRST_NAME]: InstitutionMailTemplate.mapFirstNameVar(user),
    [MailTemplateVars.LAST_NAME]: InstitutionMailTemplate.mapLastNameVar(user),
    [MailTemplateVars.CHANGED_REGISTRATIONS]: participations
      .map(participation =>
        InstitutionMailTemplate.mapChangedRegistrationVar({
          productBefore: participation.product,
          newState: participation.state,
          newWaitingListPlace: participation.waitingListPlace,
          participation,
          institution,
          englishVersion: false,
        }),
      )
      .join(''),
    [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: participations
      .map(participation =>
        InstitutionMailTemplate.mapChangedRegistrationVar({
          productBefore: participation.product,
          newState: participation.state,
          newWaitingListPlace: participation.waitingListPlace,
          participation,
          institution,
          englishVersion: true,
        }),
      )
      .join(''),
    [MailTemplateVars.OTHER_REGISTRATIONS]: InstitutionMailTemplate.mapOtherRegistrationsVar({
      participations,
      institution,
      products: otherProducts,
      englishVersion: false,
    }),
    [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
  };

  const notificationType = getNotificationType(user, mailTemplate);
  if (notificationType === 'whatsapp') {
    await sendWhatsapp({
      to: user.whatsappPhone,
      type: MessageType.TEMPLATE,
      content: {
        template: {
          name: getWhatsappTemplate(MailTemplateType.NACHRUECKPLATZ_VERFUEGBAR).name,
          language: {
            code: 'de',
          },
          components: [
            {
              type: ComponentType.BODY,
              parameters: [
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapFirstNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapLastNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: participations.map(participation => participation.product?.name).join(', ') || '-',
                },
              ],
            },
          ],
        },
      },
    });
  } else if (notificationType === 'email') {
    const content = await mailTemplate.buildEmail(mailVars as any);
    const subject = await mailTemplate.buildSubject(mailVars as any);

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    await sendInstitutionEmail({
      email: user.email,
      subject,
      content,
      institutionID,
    });
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
